'use client';

import DOMPurify from 'dompurify';
import { Config } from 'dompurify';
import {
  ComponentPropsWithoutRef,
  ElementType,
  useEffect,
  useState,
} from 'react';

import { WITH_LINK_SANITIZE_CONFIG } from './constants';

type SanitizerProps<TTag extends ElementType> = {
  /**
   * Исходная HTML-строка, которую необходимо очистить и отобразить
   */
  htmlContent: string;
  /**
   * CSS-класс, применяемый к элементу-обёртке.
   */
  className?: string;
  /**
   * HTML-тег обёртки.
   * @default 'div'
   */
  as?: ElementType;
  /**
   * Настройки для управления поведением DOMPurify
   */
  config?: Config;
} & Omit<
  ComponentPropsWithoutRef<TTag>,
  'as' | 'children' | 'dangerouslySetInnerHTML'
>;

/**
 * Компонент для безопасного рендеринга HTML-контента.
 *
 * Очищает переданный HTML перед вставкой в DOM через `dangerouslySetInnerHTML`,
 * предотвращая XSS-атаки.
 *
 * @example
 * <Sanitizer as="span" htmlContent="<b>inline</b> контент" />
 */
export const Sanitizer = <T extends ElementType>({
  htmlContent,
  config = WITH_LINK_SANITIZE_CONFIG,
  className,
  as: Tag = 'div',
  ...restProps
}: SanitizerProps<T>) => {
  const [cleanHtml, setCleanHtml] = useState<string | null>(null);

  useEffect(() => {
    setCleanHtml(DOMPurify.sanitize(htmlContent, config));
  }, [htmlContent, config]);

  if (cleanHtml === null) {
    return null;
  }

  return (
    <Tag
      dangerouslySetInnerHTML={{ __html: cleanHtml }}
      className={className}
      {...restProps}
    />
  );
};
